"use client";

import { Blog } from "@knowt/syncing/graphql/schema";
import Image from "next/image";
import Link from "@/components/wrappers/Link";
import styles from "../styles/bloghome.module.css";
import { borderRadius } from "@/utils/borderRadius";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { useCallback, useRef, useState } from "react";
import { useIsomorphicLayoutEffect } from "react-use";
import { assertTruthy } from "@knowt/syncing/utils/assertions";
import useDOMEventListener from "@/hooks/useDOMEventListener";
import { useDebouncedCallback } from "@knowt/syncing/utils/hooks/useDebouncedCallback";
import { remToPx } from "@/utils/domUtils";
import clsx from "clsx";
import { ASSETS_URL } from "@/config/deployConstants";
import { convertUnixToDateTime } from "@knowt/syncing/utils/dateTimeUtils";

type Props = {
    blog: Blog;
    isOwner: boolean;
    containerStyle?: React.CSSProperties;
    tagStyle?: React.CSSProperties;
    hideSummary?: boolean;
    showCreatedAt?: boolean;
};

export const BlogCard = ({ blog, isOwner, containerStyle, tagStyle, hideSummary, showCreatedAt }: Props) => {
    const titleAndSummaryContainerRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLParagraphElement>(null);

    const [summaryLinesCount, setSummaryLinesCount] = useState(10);

    const updateSummaryLinesCount = useCallback(() => {
        assertTruthy(titleAndSummaryContainerRef.current, "titleAndSummaryContainerRef was not set");
        assertTruthy(titleRef.current, "titleRef was not set");

        const [titleMarginTop, titleMarginBottom] = ["margin-top", "margin-bottom"].map(property => {
            assertTruthy(titleRef.current, "titleRef was not set");
            return Number.parseFloat(window.getComputedStyle(titleRef.current).getPropertyValue(property));
        });

        const titleVerticalSpace = titleRef.current.clientHeight + titleMarginTop + titleMarginBottom;

        const summaryLineHeight = remToPx("2rem");
        const summaryAvailableHeight = titleAndSummaryContainerRef.current.clientHeight - titleVerticalSpace;

        setSummaryLinesCount(Math.floor(summaryAvailableHeight / summaryLineHeight));
    }, []);

    useIsomorphicLayoutEffect(() => {
        updateSummaryLinesCount();
    }, [updateSummaryLinesCount]);

    useDOMEventListener({
        on: "resize",
        handler: useDebouncedCallback(() => updateSummaryLinesCount(), 250),
    });

    return (
        <Link
            className={styles.blogCard}
            href={isOwner ? `/blog/edit/${blog.blogId}` : `/blog/${blog.slug}`}
            style={{
                textDecoration: "inherit",
                color: themeColors.neutralBlack,
                display: "flex",
                flexDirection: "column",
                backgroundColor: themeColors.neutralWhite,
                borderRadius: borderRadius.card,
                padding: spacing.SM,
                gap: spacing.SM,
                ...containerStyle,
            }}
            data-testid="auto-id-d3c74f34-2657-423b-915f-179e41086999">
            <div
                style={{
                    borderRadius: borderRadius.card,
                    overflow: "hidden",
                    position: "relative",
                    aspectRatio: 16 / 9,
                }}
                data-testid="auto-id-dfeaba78-11db-47d3-85c0-ffda99abc496">
                <Image
                    fill
                    src={blog.coverImage?.url ?? `${ASSETS_URL}/images/blog-filler.png`}
                    alt="Blog image"
                    style={{ objectFit: "cover" }}
                />
            </div>
            {blog.tags && (
                <p
                    className="secondaryTextBold1"
                    style={{
                        backgroundColor: themeColors.background,
                        padding: `${spacing.XS} ${spacing.SM}`,
                        borderRadius: borderRadius.shortInput,
                        width: "max-content",
                        ...tagStyle,
                    }}
                    data-testid="auto-id-57d31343-add6-4ab1-9a0f-1d59f27c2ac2">
                    {blog.tags}
                </p>
            )}
            <div
                ref={titleAndSummaryContainerRef}
                style={{ display: "inline", overflow: "hidden", flex: 1 }}
                data-testid="auto-id-4df41a13-f487-4939-9b98-ddea5cb8e6cc">
                <p
                    ref={titleRef}
                    className={clsx(styles.title, "bodyBold1", "clampText")}
                    style={{ marginBottom: spacing.SM, WebkitLineClamp: 6, overflow: "hidden" }}
                    data-testid="auto-id-29cf7e8c-ad9b-410f-929c-7935fa4923d4">
                    {blog.title}
                </p>
                {!hideSummary && summaryLinesCount > 0 && (
                    <p
                        className={clsx("secondaryText1", "clampText")}
                        style={{ WebkitLineClamp: summaryLinesCount, wordBreak: "break-all" }}
                        data-testid="auto-id-d2b765a0-201d-4178-b74a-83ffc49da01c">
                        {blog.summary + "..."}
                    </p>
                )}
            </div>
            {showCreatedAt && <p
                className="secondaryText1"
                data-testid="auto-id-2e808380-020c-4fc0-a82b-eae85a9de8b5">{convertUnixToDateTime(Number(blog.created)).date}</p>}
        </Link>
    );
};
